import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CalendarClock } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import ScrollReveal from "@/components/ui/scroll-reveal";

const DEADLINE = new Date("2026-04-16T14:00:00+02:00");

interface ServizioCivileBannerProps {
  variant?: "full" | "compact";
}

const ServizioCivileBanner = ({ variant = "full" }: ServizioCivileBannerProps) => {
  const { t } = useLanguage();

  if (new Date() > DEADLINE) return null;

  const daysLeft = Math.max(0, Math.ceil((DEADLINE.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));

  if (variant === "compact") {
    return (
      <ScrollReveal>
        <section className="py-8 px-4 sm:px-6 bg-accent/10 border-y border-accent/20">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-center sm:text-left">
            <div className="flex items-center gap-3">
              <CalendarClock className="w-8 h-8 text-accent shrink-0" />
              <div>
                <h3 className="text-base font-bold font-heading">{t.servizioCivile.bannerTitle}</h3>
                <p className="text-sm text-muted-foreground">
                  {t.servizioCivile.bannerDeadline} · <span className="font-semibold text-accent">{daysLeft} {t.servizioCivile.bannerDaysLeft}</span>
                </p>
              </div>
            </div>
            <Button variant="cta" size="sm" className="shrink-0" asChild>
              <Link to="/servizi/servizio-civile">{t.servizioCivile.bannerCta}</Link>
            </Button>
          </div>
        </section>
      </ScrollReveal>
    );
  }

  return (
    <ScrollReveal>
      <div className="avps-card border-accent/30 bg-accent/5 overflow-hidden mb-8">
        <div className="grid md:grid-cols-[280px_1fr] gap-6 items-center">
          <div className="relative">
            <img
              src="/images/servizio-civile-2026.jpg"
              alt="Bando Servizio Civile 2026 ANPAS"
              className="rounded-xl w-full object-cover aspect-[7/10] shadow-md"
              loading="lazy"
            />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-accent text-accent-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                {t.servizioCivile.bannerBadge}
              </span>
              <span className="text-sm font-semibold text-accent">
                {daysLeft} {t.servizioCivile.bannerDaysLeft}
              </span>
            </div>
            <h3 className="text-xl font-bold font-heading mb-2">{t.servizioCivile.bannerTitle}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed mb-2">{t.servizioCivile.bannerText}</p>
            <p className="text-sm mb-4">
              <CalendarClock className="w-4 h-4 inline mr-1 text-accent" />
              <span className="font-semibold">{t.servizioCivile.bannerDeadline}</span>
            </p>
            <div className="flex flex-wrap gap-3">
              <Button variant="cta" asChild>
                <a href="https://domandaonline.serviziocivile.it" target="_blank" rel="noopener noreferrer">
                  {t.servizioCivile.bannerApply}
                </a>
              </Button>
              <Button variant="outline" asChild>
                <a href="https://www.anpas.org/serviziocivile26/" target="_blank" rel="noopener noreferrer">
                  {t.servizioCivile.bannerInfo}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
};

export default ServizioCivileBanner;
